import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Database, Loader2, RefreshCw, AlertTriangle, CheckCircle2, ShieldCheck, Wrench, Search, UserCheck, ArrowRight,
  Upload, FileSpreadsheet, Trash2,
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  // Memoise so the axios instance is stable across renders (otherwise effects that
  // depend on `api` would re-run forever).
  return useMemo(
    () => axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );
};

// ---- Import Mumineen Database (Area / Sub_Sector Excel format) ----
function ImportMumineenCard() {
  const api = useApi();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('full'); // none | members | full
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const destructive = mode === 'members' || mode === 'full';
  const canRun = file && !busy && (!destructive || confirmText.trim().toUpperCase() === 'REPLACE');

  const run = async () => {
    if (!file) { toast.info('Choose an .xlsx file first'); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/admin/import-mumineen-database?wipe=${mode}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.members_created} members in ${r.data.families_created} families`);
      setConfirmText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-amber-200" data-testid="import-mumineen-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-800">
          <Upload className="w-5 h-5" /> Upload Mumineen Database
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-slate-600 leading-relaxed">
          Upload the <strong>NAGPUR Mumineen Database</strong> Excel export. Required columns:{' '}
          <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">ITS_ID, Full_Name, HOF_FM_TYPE, Age, Gender, Marital_Status, Mobile, HOF_ID, Area, Misaq, Sub_Sector</code>.
          <br />Area maps to a top-level group: <strong>BD → ITWARI</strong>, <strong>BS → SADAR</strong>, <strong>Ka → KATOL</strong>.
          The Sub_Sector name becomes the <strong>Mohalla</strong> and its trailing number the <strong>Sector</strong> (e.g. <em>"BD- Fakhri 1" → Mohalla "Fakhri", Sector "Sector 1"</em>).
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white cursor-pointer hover:bg-slate-50 text-sm" data-testid="import-file-label">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>{file ? file.name : 'Choose .xlsx file'}</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            />
          </label>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Import mode</p>
          {[
            { v: 'none', label: 'Append', desc: 'Add to existing data (no deletion)' },
            { v: 'members', label: 'Replace Mumineen DB', desc: 'Clear members, families, mohallas, sectors & HOF logins first' },
            { v: 'full', label: 'Full Fresh Start', desc: 'Also clear all tokens, seats, vaccination, zabhiyat, lists …' },
          ].map((opt) => (
            <label key={opt.v} className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer text-sm ${mode === opt.v ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`} data-testid={`import-mode-${opt.v}`}>
              <input type="radio" name="import-mode" className="mt-1" checked={mode === opt.v} onChange={() => { setMode(opt.v); setConfirmText(''); }} />
              <span>
                <span className="font-medium text-slate-800 flex items-center gap-1">
                  {opt.v !== 'none' && <Trash2 className="w-3.5 h-3.5 text-red-500" />}{opt.label}
                </span>
                <span className="block text-xs text-slate-500">{opt.desc}</span>
              </span>
            </label>
          ))}
        </div>

        {destructive && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 space-y-2">
            <p className="text-xs text-red-700 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> This permanently deletes data. Type <strong>REPLACE</strong> to confirm.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="REPLACE"
              className="max-w-[200px]"
              data-testid="import-confirm-input"
            />
          </div>
        )}

        <Button onClick={run} disabled={!canRun} className="bg-amber-600 hover:bg-amber-700" data-testid="import-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
          {busy ? 'Importing…' : 'Import Database'}
        </Button>

        {result && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 space-y-1" data-testid="import-result">
            <p className="flex items-center gap-1 font-medium"><CheckCircle2 className="w-4 h-4" /> Import complete</p>
            <p>Families: <strong>{result.families_created}</strong> · Members: <strong>{result.members_created}</strong> · Sectors: <strong>{result.sectors_created}</strong> · Skipped rows: {result.rows_skipped}</p>
            <p>Mohallas: {(result.mohallas || []).join(', ') || '—'}</p>
            {result.collections_cleared?.length > 0 && (
              <p className="text-xs text-emerald-700">Cleared {result.collections_cleared.length} collection(s).</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function DataMaintenancePage() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/families/non-standard-ids');
      setProposals(r.data?.proposals || []);
      setSkipped(r.data?.skipped || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const execute = async () => {
    const eligible = proposals.filter((p) => !p.clash);
    if (eligible.length === 0) {
      toast.info('Nothing to migrate.');
      return;
    }
    if (!window.confirm(
      `This will rename ${eligible.length} famil${eligible.length === 1 ? 'y' : 'ies'} to the HOF-<ITS> format and cascade-update all references (members, sessions, bookings, etc.). Continue?`,
    )) return;
    setExecuting(true);
    try {
      const r = await api.post('/admin/families/normalise-ids?dry_run=false');
      setLastRun(r.data);
      toast.success(`Migrated ${r.data?.executed_count || 0} famil${(r.data?.executed_count || 0) === 1 ? 'y' : 'ies'}.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Migration failed');
    } finally {
      setExecuting(false);
    }
  };

  const eligibleCount = proposals.filter((p) => !p.clash).length;
  const clashCount = proposals.filter((p) => p.clash).length;

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <Database className="w-6 h-6 text-emerald-700" /> Data Maintenance
          </h1>
          <p className="text-sm text-slate-500 mt-1">Admin-only tools to keep stored data consistent.</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="refresh-maint">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </div>

      <MemberRepairCard />

      <ImportMumineenCard />

      <BackfillItsCard />

      <Card className="border-emerald-100">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2 text-emerald-800">
              <Wrench className="w-5 h-5" /> Normalise HOF Family IDs
            </span>
            <Badge variant="outline" className="bg-slate-50">
              {loading ? '…' : `${proposals.length} non-standard`}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-slate-600 leading-relaxed">
            Every family should have an id like <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">HOF-&lt;ITS&gt;</code>{' '}
            (e.g. <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">HOF-50462579</code>). Families created
            without an ITS get random hex ids like <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">HOF-648643ED</code>{' '}
            which can cause login issues. Run this migration once to rename them all. References across{' '}
            <strong>members, hof_passwords, hof_sessions, hof_clearance, seat_bookings, seat_cancellation_requests, seat_locks, toloba_contributions</strong>{' '}
            are updated automatically. Audit-logged. Idempotent — safe to re-run.
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : proposals.length === 0 && skipped.length === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3" data-testid="maint-all-good">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-emerald-900">All clear</div>
                <div className="text-sm text-emerald-800">Every family already follows the <code>HOF-&lt;ITS&gt;</code> convention.</div>
              </div>
            </div>
          ) : (
            <>
              {proposals.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600 font-semibold flex items-center justify-between">
                    <span>Will be renamed ({eligibleCount}{clashCount ? ` + ${clashCount} clashes` : ''})</span>
                    <span className="text-slate-500 normal-case font-normal">Old → New</span>
                  </div>
                  <div className="overflow-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-white sticky top-0 border-b">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-xs text-slate-600">HOF Name</th>
                          <th className="px-2 py-1.5 text-left text-xs text-slate-600">ITS</th>
                          <th className="px-2 py-1.5 text-left text-xs text-slate-600">Current ID</th>
                          <th className="px-2 py-1.5 text-left text-xs text-slate-600">New ID</th>
                          <th className="px-2 py-1.5 text-center text-xs text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {proposals.map((p) => (
                          <tr key={p.current_id} data-testid={`maint-row-${p.current_id}`}>
                            <td className="px-2 py-1.5">{p.hof_name || '—'}</td>
                            <td className="px-2 py-1.5 font-mono text-xs">{p.hof_its_id}</td>
                            <td className="px-2 py-1.5 font-mono text-xs text-rose-700">{p.current_id}</td>
                            <td className="px-2 py-1.5 font-mono text-xs text-emerald-700">{p.proposed_id}</td>
                            <td className="px-2 py-1.5 text-center">
                              {p.clash ? (
                                <Badge variant="outline" className="text-amber-800 border-amber-400 bg-amber-50">
                                  <AlertTriangle className="w-3 h-3 mr-1" /> Clash — will skip
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-emerald-800 border-emerald-400 bg-emerald-50">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Ready
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {skipped.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600 font-semibold">
                    Skipped — no usable HOF ITS ({skipped.length})
                  </div>
                  <div className="overflow-auto max-h-48">
                    <table className="w-full text-sm">
                      <tbody className="divide-y">
                        {skipped.map((s) => (
                          <tr key={s.family_id} className="text-slate-600">
                            <td className="px-2 py-1.5 font-mono text-xs">{s.family_id}</td>
                            <td className="px-2 py-1.5">{s.hof_name || '—'}</td>
                            <td className="px-2 py-1.5 text-xs italic">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Action is logged in Audit Logs. Old id is stored on the family doc as <code>previous_id</code>.
                </div>
                <Button
                  onClick={execute}
                  disabled={executing || eligibleCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="run-normalise-btn"
                >
                  {executing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wrench className="w-4 h-4 mr-1" />}
                  Run Migration ({eligibleCount})
                </Button>
              </div>

              {lastRun && (
                <div className="border rounded-md bg-slate-50 p-3 text-xs">
                  <div className="font-semibold text-slate-700 mb-1.5">Last run summary</div>
                  <div className="text-slate-600">Executed: <strong className="text-emerald-700">{lastRun.executed_count}</strong> · Skipped: <strong className="text-amber-700">{lastRun.skipped_count}</strong></div>
                  {(lastRun.executed || []).slice(0, 20).map((e, i) => (
                    <div key={i} className="font-mono text-[11px] text-slate-600 mt-1">
                      <span className="text-rose-700">{e.old_id}</span> → <span className="text-emerald-700">{e.new_id}</span>
                      {' · refs: '}
                      {Object.entries(e.ref_counts || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}=${v}`).join(', ') || 'none'}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BackfillItsCard() {
  const api = useApi();
  const [preview, setPreview] = useState(null);
  const [loadingP, setLoadingP] = useState(false);
  const [executing, setExecuting] = useState(false);

  const doPreview = async () => {
    setLoadingP(true);
    setPreview(null);
    try {
      const r = await api.post('/admin/families/backfill-missing-its?dry_run=true');
      setPreview(r.data);
      const n = r.data?.would_update_count || 0;
      if (n === 0) toast.success('No families with missing ITS — all clean.');
      else toast.info(`${n} families can be auto-filled. Review below, then click Apply.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setLoadingP(false); }
  };

  const doApply = async () => {
    if (!preview) return;
    if (!window.confirm(`Backfill hof_its_id on ${preview.would_update_count} famil${preview.would_update_count === 1 ? 'y' : 'ies'}? This makes ITS search work for those HOFs.`)) return;
    setExecuting(true);
    try {
      const r = await api.post('/admin/families/backfill-missing-its?dry_run=false');
      toast.success(`Backfilled ${r.data?.updated_count || 0} famil${(r.data?.updated_count || 0) === 1 ? 'y' : 'ies'}.`);
      setPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Backfill failed'); }
    finally { setExecuting(false); }
  };

  return (
    <Card className="border-sky-100" data-testid="backfill-its-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sky-800">
          <Wrench className="w-5 h-5" /> Backfill missing HOF ITS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600 leading-relaxed">
          Families whose <code className="bg-slate-100 px-1 rounded">hof_its_id</code> is empty show <code className="bg-slate-100 px-1 rounded">—</code> on the Clearance / Seat list, and <strong>ITS search misses them</strong>. This tool finds those families and fills their ITS from the tagged HOF member.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={doPreview} disabled={loadingP} className="bg-sky-700 hover:bg-sky-800" data-testid="backfill-preview-btn">
            {loadingP ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />} Preview
          </Button>
          {preview?.would_update_count > 0 && (
            <Button onClick={doApply} disabled={executing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="backfill-apply-btn">
              {executing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Apply ({preview.would_update_count})
            </Button>
          )}
        </div>
        {preview && (
          <div className="border rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600 font-semibold">
              {preview.would_update_count} resolvable · {preview.skipped_count} skipped
            </div>
            <div className="overflow-auto max-h-72">
              <table className="w-full text-sm">
                <thead className="bg-white sticky top-0 border-b">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs text-slate-600">HOF Name</th>
                    <th className="px-2 py-1.5 text-left text-xs text-slate-600">Family ID</th>
                    <th className="px-2 py-1.5 text-left text-xs text-slate-600">Resolved ITS</th>
                    <th className="px-2 py-1.5 text-left text-xs text-slate-600">Via</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(preview.proposals || []).map((p) => (
                    <tr key={p.family_id}>
                      <td className="px-2 py-1.5">{p.hof_name || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{p.family_id}</td>
                      <td className="px-2 py-1.5 font-mono text-xs text-emerald-700">{p.resolved_its}</td>
                      <td className="px-2 py-1.5 text-xs italic text-slate-500">{p.resolved_via}</td>
                    </tr>
                  ))}
                  {(preview.skipped || []).map((s) => (
                    <tr key={s.family_id} className="text-slate-500">
                      <td className="px-2 py-1.5">{s.hof_name || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{s.family_id}</td>
                      <td className="px-2 py-1.5 text-xs italic" colSpan={2}>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MemberRepairCard() {
  const api = useApi();
  const [its, setIts] = useState('');
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reassignTo, setReassignTo] = useState('');

  const doLookup = async () => {
    const v = (its || '').trim();
    if (!v) { toast.info('Enter member ITS'); return; }
    setBusy(true);
    setLookup(null);
    try {
      const r = await api.get(`/admin/members/lookup-by-its?its=${encodeURIComponent(v)}`);
      setLookup(r.data);
      if (!r.data?.matches?.length) toast.error('No member with that ITS');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Lookup failed'); }
    finally { setBusy(false); }
  };

  const reactivate = async (memberIts) => {
    if (!window.confirm(`Reactivate member ${memberIts}? They will reappear on HOF Portal, Seat Management and all reports.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/members/reactivate?its=${encodeURIComponent(memberIts)}`);
      if (r.data?.no_op) toast.info('Member is already active');
      else toast.success(`Reactivated: ${r.data?.member_name}`);
      await doLookup();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reactivation failed'); }
    finally { setBusy(false); }
  };

  const setAsHof = async (m) => {
    const cur = m.current_family;
    if (!cur) { toast.error('Member has no family — reassign them first.'); return; }
    if (!window.confirm(
      `Make ${m.full_name} (ITS ${m.its_id}) the HOF of family "${cur.hof_name}"? ` +
      `This will replace the current HOF ITS ${cur.hof_its_id || '(unset)'} on the family. ` +
      `It will also mark this member's relationship as "HOF".`
    )) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/families/set-hof', { member_its: m.its_id, family_id: cur.id });
      toast.success(`HOF set: ${r.data?.new_hof_name} (ITS ${r.data?.new_hof_its})`);
      await doLookup();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to set HOF'); }
    finally { setBusy(false); }
  };

  const reassign = async (memberIts) => {
    const target = (reassignTo || '').trim();
    if (!target) { toast.info('Enter target HOF ITS'); return; }
    if (!window.confirm(`Move member ${memberIts} to family of HOF ITS ${target}? Their mohalla/sector will be inherited from the new family.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/members/reassign-to-family', { member_its: memberIts, target_hof_its: target });
      if (r.data?.no_op) toast.info('Member is already in this family');
      else toast.success(`Moved to: ${r.data?.to_hof_name}`);
      await doLookup();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reassign failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-amber-100" data-testid="member-repair-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-800">
          <UserCheck className="w-5 h-5" /> Find &amp; Repair a Member
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600 leading-relaxed">
          Use this when an HOF reports &ldquo;my family member is tagged but not showing on my page&rdquo;. The two most common causes are: the member is marked <strong>inactive</strong>, or their <code className="bg-slate-100 px-1 rounded">hof_id</code> still points to their old family.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Member ITS (e.g. 30384975)"
            value={its}
            onChange={(e) => setIts(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doLookup()}
            className="max-w-xs"
            data-testid="member-lookup-its"
          />
          <Button onClick={doLookup} disabled={busy} className="bg-amber-700 hover:bg-amber-800" data-testid="member-lookup-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />} Lookup
          </Button>
        </div>

        {lookup?.matches?.map((m) => (
          <div key={m.member_id} className="border rounded-md p-3 bg-slate-50 space-y-2" data-testid={`member-result-${m.its_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-semibold text-slate-800">{m.full_name}</div>
              <Badge variant="outline" className="text-xs">ITS {m.its_id}</Badge>
              <Badge variant="outline" className="text-xs">{m.gender}</Badge>
              <Badge variant="outline" className="text-xs">Misaq: {m.misaq_status || '—'}</Badge>
              <Badge className={`text-xs ${(m.status || '').toLowerCase() === 'inactive' ? 'bg-red-600' : 'bg-emerald-600'}`}>
                {m.status || 'active'}
              </Badge>
            </div>
            {m.spouse_its_id && <div className="text-xs text-slate-600">Spouse ITS: <span className="font-mono">{m.spouse_its_id}</span></div>}
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div className="bg-white border rounded p-2">
                <div className="text-slate-500 uppercase tracking-wide font-semibold text-[10px] mb-1">Current Family</div>
                {m.current_family ? (
                  <>
                    <div className="font-medium">{m.current_family.hof_name}</div>
                    <div className="text-slate-500 font-mono text-[10px]">HOF ITS {m.current_family.hof_its_id || '(unset)'}</div>
                    {m.current_family.hof_its_id && m.current_family.hof_its_id !== m.its_id && (
                      <div className="mt-1 text-[10px] text-amber-700 italic">
                        Stored HOF ITS differs from this member&rsquo;s ITS.
                      </div>
                    )}
                  </>
                ) : <div className="text-rose-600 font-medium">No family linked</div>}
              </div>
              <div className="bg-white border rounded p-2">
                <div className="text-slate-500 uppercase tracking-wide font-semibold text-[10px] mb-1">Spouse&rsquo;s Family (suggested)</div>
                {m.spouse_family ? (
                  <>
                    <div className="font-medium">{m.spouse_family.hof_name}</div>
                    <div className="text-slate-500 font-mono text-[10px]">HOF ITS {m.spouse_family.hof_its_id}</div>
                  </>
                ) : <div className="text-slate-400 italic">—</div>}
              </div>
            </div>
            {m.needs_reassign && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Current family differs from spouse&rsquo;s family.
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {(m.status || '').toLowerCase() === 'inactive' && (
                <Button size="sm" onClick={() => reactivate(m.its_id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`reactivate-${m.its_id}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Reactivate
                </Button>
              )}
              {m.current_family && m.current_family.hof_its_id !== m.its_id && (
                <Button size="sm" onClick={() => setAsHof(m)} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`set-hof-${m.its_id}`} title="Replace this family's HOF ITS with this member">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Make this member the HOF
                </Button>
              )}
              <Input
                placeholder="Target HOF ITS (e.g. 20320345)"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
                className="max-w-[200px] h-8 text-xs"
                data-testid={`reassign-target-${m.its_id}`}
              />
              <Button size="sm" variant="outline" onClick={() => reassign(m.its_id)} disabled={busy} data-testid={`reassign-${m.its_id}`}>
                <ArrowRight className="w-3 h-3 mr-1" /> Move to Family
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
